import { DeploymentIntegrationType, type Deployment } from "../api";
import { callApiObservable } from "@/api";
import {
  getConversationApi,
  listVisitorMessagesApi,
  sendMessageFeedbackApi,
  submitMessageFormApi,
} from "@/api/messagingApi";
import type { FormValue } from "@/model/form";
import {
  type MessagingMessage,
  type MessagingConversation,
  type MessageFeedbackType,
  toMessageFeedback,
} from "@/model/messaging";
import { BaseStore, createAtomStore } from "@deputy-ai/common/jotai";
import { atom } from "jotai";
import { finalize, map, tap } from "rxjs";

export interface ConversationStoreOptions {
  deployment: Deployment;
  conversationUid: string;
}

export class ConversationStore extends BaseStore {
  get deployment() {
    return this.options.deployment;
  }
  get conversationUid() {
    return this.options.conversationUid;
  }

  conversation = atom<MessagingConversation>();

  loading = atom(false);

  hasHumanAgentHandOffed = atom((get) => {
    const conversation = get(this.conversation);
    const integrationName = conversation?.metadata?.current_integration_name;
    const botIntegrationName = this.deployment.config.integrations?.find(
      (item) => item.type === DeploymentIntegrationType.ACTIVE
    )?.name;
    if (!integrationName) {
      return false;
    }
    return integrationName !== botIntegrationName;
  });

  constructor(private readonly options: ConversationStoreOptions) {
    super();
  }

  init() {
    return this.loadConversation().pipe(map((res) => res.conversation));
  }

  loadConversation() {
    this.set(this.loading, true);
    return callApiObservable(async () => {
      return await getConversationApi({
        path: {
          orgUid: this.deployment.orgUid,
          installedAppUid: this.deployment.installedAppUid!,
          conversationUid: this.conversationUid,
        },
      });
    }).pipe(
      tap((res) => this.set(this.conversation, res.conversation)),
      finalize(() => this.set(this.loading, false))
    );
  }

  listMessages(params: { page: number; pageSize: number }) {
    const { page, pageSize } = params;
    return callApiObservable(
      async ({ controller }) =>
        await listVisitorMessagesApi({
          signal: controller.signal,
          path: {
            orgUid: this.deployment.orgUid,
            installedAppUid: this.deployment.installedAppUid!,
            conversationUid: this.conversationUid,
          },
          query: {
            page,
            pageSize,
          },
        })
    );
  }

  sendMessageFeedback(messageUid: string, feedbackType: MessageFeedbackType) {
    const params = {
      orgUid: this.deployment.orgUid,
      installedAppUid: this.deployment.installedAppUid!,
      conversationUid: this.conversationUid,
    };
    const payload = toMessageFeedback(feedbackType);

    return callApiObservable(
      async () =>
        await sendMessageFeedbackApi({
          path: { ...params },
          body: {
            ...params,
            messageUid,
            feedback: payload,
          },
        })
    ).pipe(map((res) => res.message as MessagingMessage));
  }

  submitForm(messageUid: string, formData: Record<string, FormValue>) {
    const params = {
      orgUid: this.deployment.orgUid,
      installedAppUid: this.deployment.installedAppUid!,
      conversationUid: this.conversationUid,
    };
    return callApiObservable(async () => {
      return await submitMessageFormApi({
        path: {
          ...params,
        },
        body: {
          ...params,
          messageUid,
          formData,
        },
      });
    });
  }
}

export const { ConversationProvider, useConversationStore } = createAtomStore({
  name: "Conversation",
  StoreClass: ConversationStore,
});
